// moduleRegistryBridge.js — capability-scoped external company-registry
// lookups for workspace MODULES (bellforge D2-1). The registry-plane sibling
// of moduleAiBridge.js: the gate is the module's server-granted
// `capabilities.registry` (workspace.settings.workspaceModule.capabilities
// .registry — written by the workspace owner/admin, never by the module).
//
// The loader (workspaceProjects.js) registers the built bridge as the
// `moduleRegistryLookup` context function, so module functions reach the
// server's registry proxy via
//   el.call('moduleRegistryLookup', op, args)
//     op 'search'  → args { q, limit? }        → { ok, rows, provider }
//     op 'company' → args { siren }            → { ok, company, provider }
//     op 'status'  → args {}                   → { ok, mode, providers }
// — never through raw fetch (the publish-time module audit forbids it, and
// provider keys must stay server-side). Transport: sdk RegistryService →
// GET /core/registry/fr/* (provider chain + 1h cache live on the server).
//
// Returns plain result objects `{ ok, ...fields }` / `{ ok:false, error }`
// (never throws) so the module's discovery surface degrades to sim-only
// rows gracefully. Same live-grant heal as the ai bridge: the load-time
// capability snapshot may predate the settings record (cold-boot race), so
// the grant is re-read at CALL time; the server enforces auth regardless —
// this client gate is UX, not security.

import _wsShared from '@symbo-ls/workspace-shared'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)
const shellWorkspaceSettings = (...args) => _wsShared.functions.workspaceSettings(...args)

const _liveRegistryCap = (root, snapshot) => {
  try {
    const { settings } = shellWorkspaceSettings(root)
    const cap = settings && settings.workspaceModule && settings.workspaceModule.capabilities
    if (cap && cap.registry) return cap.registry
  } catch (e) {
    // settings accessor unavailable mid-boot — snapshot decides
  }
  return snapshot || null
}

// Build the scoped registry facade for one module's capability grant.
// `getSdkFn` is injectable for tests; defaults to the shared getSDK().
export const buildModuleRegistryBridge = (root, capabilities = {}, { getSdkFn = shellGetSDK } = {}) => {
  const registryCap = (capabilities && capabilities.registry) || null

  return async function moduleRegistryLookup(op, args = {}) {
    const a = args || {}
    if (!_liveRegistryCap(root, registryCap)) {
      return { ok: false, error: 'Registry capability not granted.' }
    }
    const sdk = getSdkFn ? getSdkFn() : null
    if (!sdk) return { ok: false, error: 'Registry service unavailable.' }

    let registry = null
    try {
      registry =
        typeof sdk.getService === 'function' ? await sdk.getService('registry') : sdk.registry
    } catch (e) {
      registry = sdk.registry || null
    }
    if (!registry || typeof registry.frSearch !== 'function') {
      return { ok: false, error: 'Registry service unavailable.' }
    }

    try {
      if (op === 'search') {
        const data = await registry.frSearch(String(a.q || ''), {
          limit: a.limit != null ? a.limit : undefined
        })
        return { ok: true, rows: (data && data.rows) || [], provider: data && data.provider }
      }
      if (op === 'company') {
        const data = await registry.frCompany(String(a.siren || ''))
        return {
          ok: true,
          company: (data && data.company) || null,
          provider: data && data.provider
        }
      }
      if (op === 'status') {
        const data = await registry.frStatus()
        return {
          ok: true,
          mode: (data && data.mode) || 'live',
          providers: (data && data.providers) || {}
        }
      }
      return { ok: false, error: 'Unknown op: ' + op }
    } catch (e) {
      return { ok: false, error: (e && e.message) || 'Registry request failed.' }
    }
  }
}
